<script setup>
import { ref } from 'vue'

defineProps({
  table: {
    type: Object,
    required: true
  }
})
const isShowNote = ref(false)
const nowWeekRef = ref(null)
const hasSetRef = ref(false)

const setRef = (el, content) => {
  if (!content.past && !hasSetRef.value) {
    nowWeekRef.value = el
    hasSetRef.value = true
  }
}
onMounted(() => {
  if (nowWeekRef.value) {
    nowWeekRef.value.scrollIntoView({ behavior: 'instant', inline: 'start', block: 'nearest' })
  }
})
</script>

<template>
  <div class="table">
    <ul class="head">
      <li class="date">
        <p>日</p>
      </li>
      <li class="weather">
        <p>天気</p>
      </li>
      <li class="high">
        <p>最高</p>
      </li>
      <li class="low">
        <p>最低</p>
      </li>
      <li class="rain">
        <p>降水</p>
      </li>
      <li class="status">
        <p>指数</p>
      </li>
    </ul>
    <div
      class="body"
    >
      <ul
        v-for="(content, index) in table"
        :ref="(el) => setRef(el, content)"
        :key="index"
        class="content"
        :class="content.past && 'past'"
      >
        <li
          class="date"
          :class="content.dowEn && content.dowEn"
        >
          <p class="day">
            {{ content.date }}
          </p>
          <p class="dow">
            ({{ content.dowJp }})
          </p>
          <p
            v-if="content.reliability!==null"
            class="credibility"
          >
            {{ content.reliability }}
          </p>
        </li>
        <li class="weather">
          <figure>
            <img
              :src="`https://gvs.weathernews.jp/onebox/img/wxicon/${content.weather}.webp`"
              loading="lazy"
              alt=""
            >
          </figure>
        </li>
        <li class="high">
          <p>{{ content.high === null ? '-' : Math.round(content.high) }}<span>℃</span></p>
        </li>
        <li class="low">
          <p>{{ content.low === null ? '-' : Math.round(content.low) }}<span>℃</span></p>
        </li>
        <li
          v-if="content.past"
          class="rain"
        >
          <p>{{ content.prec === null ? '-' : Math.round(content.prec) }}<span>mm</span></p>
        </li>
        <li
          v-else
          class="rain"
        >
          <p>{{ content.pop === null ? '-' : content.pop }}<span v-if="content.pop !== '0'">%</span></p>
        </li>
        <li class="status">
          <figure>
            <img
              v-if="content.golfStatus"
              :src="`https://gvs.weathernews.jp/golf/image/golf_status/icon_status_${content.golfStatus}.svg`"
              alt=""
              loading="lazy"
            >
          </figure>
        </li>
      </ul>
      <div class="note">
        <p class="note__title">
          予報の<br>信頼度
        </p>
        <div class="note__info">
          <p>高</p>
          <div class="note__rank">
            <p>A</p>
            <p>B</p>
            <p>C</p>
            <p>D</p>
            <p>E</p>
          </div>
          <p>低</p>
        </div>
        <button
          class="note__button"
          @click="isShowNote = !isShowNote"
        >
          ？
        </button>
      </div>
    </div>
  </div>
  <div
    v-if="isShowNote"
    class="note_modal"
  >
    <div class="note_modal__inner">
      <p class="title">
        予報の信頼度について
      </p>
      <p class="text">
        2週間天気の後半に表示されている「A〜E」の記号は、降水有無の予報に対する信頼度です（毎日10時頃更新）。Aの方が高い信頼度があり、予報が的中しやすく、予報が変わりにくいことを表しています。
      </p>
      <button
        class="close-button"
        @click="isShowNote = !isShowNote"
      >
        閉じる
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.table {
  display: flex;
  position: relative;
  width: 100%;
  height: 236px;
  border: solid 1px #dfe4f0;
  border-radius: 8px;
  background-color: #f9f9f9;
  overflow: hidden;
  &::after {
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    width: 10px;
    height: 100%;
    background: linear-gradient(to right, rgba(0 0 0 / 0), rgba(0 0 0 / .3));
    content: "";
  }
  .head {
    display: flex;
    position: relative;
    flex-direction: column;
    width: 45px;
    height: 100%;
    background-color: #eef1f8;
    box-shadow: 3px 0 6px 0 rgba(0 0 0 / .3);
    z-index: 2;
    .date {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 58px;
      border-bottom: solid 1px #dfe4f0;
    }
    .weather {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 38px;
      border-bottom: solid 1px #dfe4f0;
    }
    .high,
    .low,
    .rain {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 30px;
      border-bottom: solid 1px #dfe4f0;
    }
    p {
      font-size: 14px;
      line-height: 14px;
      text-align: center;
    }
    .status {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 38px;
    }
  }
  .body {
    position: relative;
    display: flex;
    flex: 1;
    overflow-x: scroll;
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    &::-webkit-scrollbar-track {
      border-radius: 8px;
      background-color: #eef1f8;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 8px;
      background: #438fec;
    }
    .content {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: max(calc(100% / 11), 52px);
      margin: 0;
      gap: 0;
      .date {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        height: 58px;
        border-bottom: solid 1px #dfe4f0;
        &.sat {
          p {
            color: #0060ff;
            &:is(.past *) {
              color: #609cfe;
            }
          }
        }
        &.hol {
          p {
            color: #ff3000;
            &:is(.past *) {
              color: #ff7e5f;
            }
          }
        }
        p {
          font-size: 15px;
          font-weight: 600;
          line-height: 15px;
          &:is(.past *) {
            color: #a3a3a3;
          }
        }
        .credibility {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 4px;
          width: 12px;
          height: 12px;
          background-color: #9FA9B3;
          color: #fff !important;
          text-align: center;
          text-shadow: 0 1px 1px rgb(0 0 0 / 0.25);
          font-family: "Helvetica Neue";
          font-size: 12px;
          font-weight: 700;
          line-height: 1em;
        }
      }
      .weather {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 38px;
        border-bottom: solid 1px #dfe4f0;
        figure {
          display: flex;
          width: 50px;
          aspect-ratio: 152 / 112;
          &:is(.past *) {
            filter: grayscale(1);
            opacity: 0.45;
          }
          img {
            width: 100%;
          }
        }
      }
      .high,
      .low,
      .rain {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 30px;
        border-bottom: solid 1px #dfe4f0;
        p {
          font-size: 15px;
          font-weight: 600;
          line-height: 15px;
          span {
            margin-left: 2px;
            font-size: 10px;
            font-weight: 300;
          }
          &:is(.past *) {
            color: #a3a3a3;
          }
        }
      }
      .high {
        p {
          color: #f64d00;
        }
      }
      .low {
        p {
          color: #0075f3;
        }
      }
      .status {
        padding-block: 4px;
        border-bottom: solid 1px #dfe4f0;
        figure {
          width: 30px;
          aspect-ratio: 1 / 1;
          margin-inline: auto;
          &:is(.past *) {
            filter: grayscale(1);
            opacity: 0.45;
          }
          img {
            width: 100%;
          }
        }
      }
    }
  }
  .note {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-width: 48px;
    background-color: #E3E6E9;
    &__title {
      color: #303030;
      text-align: center;
      font-size: 12px;
      font-weight: 600;
      line-height: 1em;
    }
    &__info {
      display: flex;
      flex-direction: column;
      gap: 4px;
      p {
        color: #303030;
        text-align: center;
        font-size: 12px;
        line-height: 1em;
      }
    }
    &__rank {
      display: flex;
      flex-direction: column;
      gap: 1px;
      p {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        background-color: #9FA9B3;
        color: #FFF;
        text-shadow: 0 0.889px 1.778px rgb(0 0 0 / 0.40);
        font-size: 14px;
        font-weight: 700;
      }
    }
    &__button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      border-radius: 2px;
      background: linear-gradient(180deg, rgb(0 0 0 / 0.33) 0%, rgb(0 0 0 / 0.66) 100%);
      color: #fff;
      font-weight: 600;
      font-size: 12px;
      line-height: 1em;
    }
  }
  @media (any-hover: hover) {
    .note {
      &__button {
        transition: opacity .2s ease-in-out;
        &:hover {
          opacity: .6;
        }
      }
    }
  }
}
.note_modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100svw;
  height: 100svh;
  z-index: 20;
  background-color: rgba(0, 0, 0, 0.25);
}
.note_modal__inner {
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: 15px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  max-width: 500px;
  max-height: 90%;
  padding: 30px;
  border-radius: 12px;
  background-color: #fff;
  .title {
    font-size: 16px;
    font-weight: 700;
  }
  .text {
    font-size: 14px;
    flex: 1;
    overflow-y: scroll;
  }
  .close-button {
    display: block;
    margin: 15px 0 0 auto;
    color: #3569c0;
    font-size: 14px;
    cursor: pointer;
  }
}
</style>
